import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardSidebar';
import api from '../services/api';

// Files are stored on the admin server, not the website server
const UPLOADS_BASE = process.env.REACT_APP_UPLOADS_BASE_URL || 'http://localhost:5011/uploads';

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
};

const StudentPYQPage = () => {
  const [papers, setPapers]           = useState([]);
  const [courses, setCourses]         = useState([]);
  const [years, setYears]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedYear, setSelectedYear]     = useState('');

  // Load courses from subscriptions
  useEffect(() => {
    api.get('/users/subscriptions').then(res => {
      const subs = res.data.data || [];
      const map = new Map();
      subs.forEach(s => { if (!map.has(s.CourseId)) map.set(s.CourseId, { CourseId: s.CourseId, CourseName: s.CourseName || s.PackTitle }); });
      setCourses(Array.from(map.values()));
    }).catch(() => {});
  }, []);

  // Fetch PYQs
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedCourse) params.set('courseId', selectedCourse);
    if (selectedYear)   params.set('examYear', selectedYear);
    params.set('limit', '50');

    api.get(`/pyq?${params}`).then(res => {
      if (res.data.success) {
        setPapers(res.data.data);
        if (res.data.years) setYears(res.data.years);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedCourse, selectedYear]);

  const handleDownload = async (item) => {
    try { await api.get(`/pyq/${item.PYQId}/download`); } catch {} // track silently
    window.open(`${UPLOADS_BASE}/${item.FilePath}`, '_blank');
  };

  // Group papers by year
  const grouped = {};
  papers.forEach(p => {
    const yr = p.ExamYear || 'Other';
    if (!grouped[yr]) grouped[yr] = [];
    grouped[yr].push(p);
  });
  const sortedYears = Object.keys(grouped).sort((a, b) => b - a);

  return (
    <DashboardLayout title="PYQ Papers">
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3"><span className="text-4xl">📄</span> Previous Year Papers</h1>
            <p className="text-text-muted mt-1">Download CUET UG, PG, and NET question papers by year</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10 mb-8 flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-text-muted text-sm mb-2">Course</label>
            <select className="w-full bg-[#2d1b4e] border border-white/20 rounded-lg p-3 text-white outline-none cursor-pointer"
              value={selectedCourse} onChange={e => setSelectedCourse(e.target.value)}>
              <option value="">All Courses</option>
              {courses.map(c => <option key={c.CourseId} value={c.CourseId}>{c.CourseName}</option>)}
            </select>
          </div>
          <div className="w-[150px]">
            <label className="block text-text-muted text-sm mb-2">Exam Year</label>
            <select className="w-full bg-[#2d1b4e] border border-white/20 rounded-lg p-3 text-white outline-none cursor-pointer"
              value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
              <option value="">All Years</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="text-sm text-text-muted">{papers.length} paper{papers.length !== 1 ? 's' : ''} found</div>
        </div>

        {/* Papers grouped by year */}
        {loading ? (
          <div className="text-center py-20 text-text-muted animate-pulse">Loading papers...</div>
        ) : papers.length === 0 ? (
          <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
            <div className="text-5xl mb-4">📄</div>
            <p className="text-text-muted">No question papers available{selectedCourse ? ' for this course' : ''}.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {sortedYears.map(year => (
              <div key={year}>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
                  <span className="w-12 h-12 bg-accent-gold/20 rounded-xl flex items-center justify-center text-accent-gold font-bold text-sm">{year}</span>
                  <span>{year} Question Papers</span>
                  <span className="text-sm text-text-muted font-normal ml-auto">{grouped[year].length} paper{grouped[year].length !== 1 ? 's' : ''}</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {grouped[year].map(p => (
                    <div key={p.PYQId} className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-accent-gold/30 transition-all group">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-red-500/20 text-red-300 text-xs px-2 py-1 rounded font-semibold">📄 PDF</span>
                        {p.TopicName && <span className="text-xs text-text-muted bg-white/10 px-2 py-1 rounded">{p.TopicName}</span>}
                      </div>

                      <h3 className="font-bold text-text-light mb-2 line-clamp-2 group-hover:text-accent-gold transition-colors">{p.Title}</h3>
                      {p.Description && <p className="text-text-muted text-sm line-clamp-2 mb-3">{p.Description}</p>}

                      <div className="flex items-center gap-3 text-xs text-text-muted mb-4">
                        <span>{p.CourseName}</span>
                        {p.FileSize && <span>• {formatBytes(p.FileSize)}</span>}
                      </div>

                      <button onClick={() => handleDownload(p)}
                        className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-purple-500/20 transition-all flex items-center justify-center gap-2">
                        📥 Download Paper
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StudentPYQPage;